import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { StopCircle } from 'lucide-react';
import GameTimer from './GameTimer';

/**
 * Minimal top overlay during active game phases.
 * Shows mode icon, prompt/turn info, countdown timer, and a host
 * escape-hatch button to end the game from any phase.
 */
export default function GameHUD({
  timerEndsAt,
  timerDuration,
  onTimerExpire,
  label,
  sublabel,
  emoji,
  children,
}) {
  const { state, dispatch } = useApp();
  const firebase = useFirebase();
  const [confirmEnd, setConfirmEnd] = useState(false);

  const handleEndGame = async () => {
    if (!confirmEnd) {
      setConfirmEnd(true);
      setTimeout(() => setConfirmEnd(false), 3000);
      return;
    }
    await firebase.clearGame();
    dispatch({ type: 'CLEAR_GAME' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Game ended — back to free draw' } });
  };

  return (
    <div className="fixed top-14 left-0 right-0 z-40 pointer-events-none">
      <div className="flex items-center justify-between px-4 py-2 mx-auto max-w-xl">
        {/* Left: info */}
        <div className="pointer-events-auto bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg border border-slate-100 flex items-center gap-3 max-w-[55%]">
          {emoji && <span className="text-xl shrink-0">{emoji}</span>}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-charcoal font-display truncate">{label}</p>
            {sublabel && <p className="text-xs text-slate-400 font-display truncate">{sublabel}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Host: end game button — always visible as escape hatch */}
          {state.user.isHost && (
            <button
              onClick={handleEndGame}
              className={`pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-display font-medium shadow-lg border transition-colors ${
                confirmEnd
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white/90 backdrop-blur-sm text-slate-400 border-slate-100 hover:text-red-500 hover:border-red-200'
              }`}
            >
              <StopCircle className="w-3.5 h-3.5" />
              {confirmEnd ? 'Tap again' : 'End'}
            </button>
          )}

          {/* Right: timer */}
          {timerEndsAt && (
            <div className="pointer-events-auto bg-white/90 backdrop-blur-sm rounded-xl p-2 shadow-lg border border-slate-100">
              <GameTimer
                timerEndsAt={timerEndsAt}
                totalDuration={timerDuration}
                onExpire={onTimerExpire}
                size="sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Optional bottom bar (e.g. submit button, reaction bar) */}
      {children && (
        <div className="fixed bottom-4 left-0 right-0 z-40 flex justify-center pointer-events-none">
          <div className="pointer-events-auto">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}
